import React, { useEffect, useState, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { fetchHVACampaignPerformanceData } from 'services/hvareporting.service';
import * as noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import KPISection from './KPISection';
import StocksSection from './StocksSection';
import { IntensitySection } from './IntensitySection';
import { AgreementsSection } from './AgreementsSection';
import CampaignPerformanceData from './CampaignPerformanceData';
import HVASpinner from 'components/base/HVASpinner';

export interface MetricData {
  type: string;
  value: number | string;
}

export interface CampaignData {
  [key: string]: MetricData;
}

export interface PerformanceData {
  bxpcampaignid: number;
  date: string;
  campaigndata: CampaignData;
}

interface CampaignPerformanceReportProps {
  bxpcampaignid: number;
  startdate: Date;
  enddate: Date;
}

const CampaignPerformanceReport: React.FC<CampaignPerformanceReportProps> = ({
  bxpcampaignid,
  startdate,
  enddate
}) => {
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [filteredData, setFilteredData] = useState<PerformanceData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const sliderInstanceRef = useRef<noUiSlider.API | null>(null);
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    startdate,
    enddate
  ]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchHVACampaignPerformanceData({
          bxpcampaignid,
          datefrom: startdate.toISOString(),
          dateto: enddate.toISOString()
        });
        setPerformanceData(data);
        setError(null);
      } catch (error) {
        setError(
          'No campaign performance data found for the selected criteria!'
        );
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [bxpcampaignid, startdate, enddate]);

  // Aquí manejamos el slider que permitirá filtrar los datos para gráficos
  useEffect(() => {
    if (
      sliderRef.current &&
      !sliderInstanceRef.current &&
      performanceData.length > 0
    ) {
      sliderInstanceRef.current = noUiSlider.create(sliderRef.current, {
        start: [0, performanceData.length - 1],
        connect: true,
        range: {
          min: 0,
          max: performanceData.length - 1
        },
        step: 1
      });

      sliderInstanceRef.current.on('update', (values: (string | number)[]) => {
        const [start, end] = values.map(value => Math.round(Number(value)));
        const startIdx = Math.min(start, performanceData.length - 1);
        const endIdx = Math.min(end, performanceData.length - 1);

        const filtered = performanceData.slice(
          startIdx,
          endIdx + 1
        ) as PerformanceData[];
        setFilteredData(filtered);

        const startData = performanceData[startIdx] as PerformanceData;
        const endData = performanceData[endIdx] as PerformanceData;

        if (startData && endData) {
          setDateRange([new Date(startData.date), new Date(endData.date)]);
        }
      });
    }

    return () => {
      if (sliderInstanceRef.current) {
        sliderInstanceRef.current.destroy();
        sliderInstanceRef.current = null;
      }
    };
  }, [performanceData]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5 pb-7">
        <HVASpinner />
      </div>
    );
  }
  if (error) return <div className="pt-4 pb-4 text-danger fs-9">{error}</div>;

  return (
    <div className="">
      {/* Slider Row */}
      <Row className="gy-3 mt-0 mb-4 justify-content-between bg-body-emphasis">
        <Col xs={12}>
          <h5>Dynamic date range filter for KPI's and charts</h5>
          <div className="px-3">
            <div className="pt-2 mb-2 d-flex justify-content-between">
              <span className="fs-10">
                {dateRange[0]?.toLocaleDateString()} -{'·'}
                {dateRange[1]?.toLocaleDateString()}
              </span>
              <span className="fs-10">{filteredData.length} days selected</span>
            </div>
            <div ref={sliderRef} className="mt-2" style={{ height: '12px' }} />
          </div>
        </Col>
      </Row>

      {/* Secciones de componentes */}
      <KPISection data={filteredData} />
      <StocksSection data={filteredData} />
      <IntensitySection data={filteredData} />
      <AgreementsSection data={filteredData} />

      {/* Aquí va la tabla completa de la selección iniicial*/}
      <Row className="gy-3 mt-0 justify-content-between bg-body-emphasis">
        <CampaignPerformanceData data={performanceData} />
      </Row>
    </div>
  );
};

export default CampaignPerformanceReport;
